<template>
  <table class="table is-fullwidth is-narrow carfax-vehicle-comparison">
    <thead>
      <tr>
        <th></th>
        <th>This record</th>
        <th>CARFAX</th>
        <th v-if="showSuggested">Suggested</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="row.label" :class="{ 'carfax-difference': row.different }">
        <td class="has-text-weight-semibold">{{ row.label }}</td>
        <td>{{ row.record }}</td>
        <td>{{ row.carfax }}</td>
        <td v-if="showSuggested" class="carfax-suggested">{{ row.suggested }}</td>
      </tr>
    </tbody>
  </table>
</template>

<script>
/*
 * The body of the CARFAX reconcile prompt.
 *
 * ⚠ Why this exists. The modal used to be one interpolated sentence quoting two long vehicle
 * descriptions - "...returned from CARFAX, 2023 Chevrolet Corvette Stingray Convertible, differs from
 * the current vehicle information, 2023 Chevrolet Corvette 2LT 2D Convertible..." - so the operator
 * had to diff two prose strings in their head to find the single field that had moved. Garrett, on
 * PPA108227: "I can't even understand this, it's almost too much blah blah blah." (31 Aug, change 5.)
 *
 * ⚠ ONE component for FOUR modals. The prompt is copy-pasted into EditInventory, NewInventory,
 * ApprovePpaAction and NewRoServiceVehicle, which is how the sentence came to be maintained in four
 * places at once. The table is written here so that cannot happen again.
 *
 * ⚠ HIGHLIGHTING COMES FROM THE CALLER'S FLAGS, never from comparing the two cells. The roService
 * screen deliberately raises the prompt on make, year and model only - its trim and style comparisons
 * are commented out - so re-deriving equality here would mark rows that screen does not treat as
 * differences. The flags are already computed by utils/carfax_make_model_comparison.
 */
import { readMakeModelFields, isMeaningfulValue } from '@/utils/carfax_make_model_resolution'

export default {
  name: 'CarfaxVehicleComparison',
  props: {
    // The record's current vehicle selection.
    selectedMakeModel: {
      type: Object,
      default: null
    },
    // The resolved carfax match, or the free-text values when nothing resolved.
    carfaxMakeModel: {
      type: Object,
      default: null
    },
    // { make, productionYear, model, trim, style } - the flags the comparison already produced.
    differences: {
      type: Object,
      default: function () {
        return {}
      }
    },
    /*
     * ⚠ Defaults TRUE so this is additive. The four inventory hosts pass nothing and keep all five
     * rows; only the two RO screens opt out, because neither trim nor style is editable there - both
     * pass showTrimStyle false to the selector itself - and the RO comparison deliberately evaluates
     * make, year and model only. Rendering two rows the operator cannot act on, permanently unmarked
     * because the flags behind them do not exist on that path, is noise.
     */
    showTrimStyle: {
      type: Boolean,
      default: true
    },
    /*
     * The learned-mapping suggestion, same shape findLearnedMapping returns, or null.
     *
     * ⚠ Additive in the way showTrimStyle already is: the column only renders when there is
     * something to put in it, so all six existing hosts keep today's two-column table without
     * passing anything.
     */
    suggestedModel: {
      type: Object,
      default: null
    }
  },
  computed: {
    rows: function () {
      //⚠ Shared with the overwrite note, so the table and the note cannot disagree about which
      //property a name is read from - including the custom* fallback used when carfax resolved no
      //catalogue row, which is the case that needs the comparison most.
      const record = readMakeModelFields(this.selectedMakeModel)
      const carfax = readMakeModelFields(this.carfaxMakeModel)
      const flags = this.differences || {}

      const rows = [
        { label: 'Year', record: this.show(record.productionYear), carfax: this.show(carfax.productionYear), different: !!flags.productionYear },
        { label: 'Make', record: this.show(record.make), carfax: this.show(carfax.make), different: !!flags.make },
        { label: 'Model', record: this.show(record.model), carfax: this.show(carfax.model), different: !!flags.model }
      ]

      if (this.showTrimStyle) {
        rows.push({ label: 'Trim', record: this.show(record.trim), carfax: this.show(carfax.trim), different: !!flags.trim })
        rows.push({ label: 'Style', record: this.show(record.style), carfax: this.show(carfax.style), different: !!flags.style })
      }

      /*
       * ⚠ Only Model and Trim carry a suggestion. The mapping records what the vehicle was FILED as,
       * which says nothing about year, make or body style - putting a value in those rows would
       * present an inference as evidence.
       */
      const suggested = this.suggestedModel
      rows.forEach((row) => {
        if (!suggested) {
          row.suggested = ''
        } else if (row.label === 'Model') {
          row.suggested = this.show(suggested.name)
        } else if (row.label === 'Trim') {
          row.suggested = this.show(suggested.trim)
        } else {
          row.suggested = ''
        }
      })

      return rows
    },
    /*
     * ⚠ Keyed on there being something to SHOW, not merely on the prop being set. A mapping whose
     * model matches and whose trim is blank has nothing to offer here, and an empty extra column
     * reads as a rendering fault.
     */
    showSuggested: function () {
      return this.rows.some((row) => !!row.suggested && row.suggested !== '-')
    }
  },
  methods: {
    //A dash rather than an empty cell, so a blank reads as "carfax said nothing" instead of as a
    //rendering fault. 'N/A' is what carfax sends for a field it has no answer for.
    show: function (value) {
      //⚠ Through the shared predicate, not a third hand-rolled copy. This test existed here, in
      //CarfaxSourceEvidence and twice in the utils, and they had already drifted - the server
      //rejected 'Not specified' and none of these did, so it rendered as if CARFAX had said it.
      return isMeaningfulValue(value) ? String(value).trim() : '-'
    }
  }
}
</script>

<style scoped>
  .carfax-vehicle-comparison {
    margin-bottom: 0;
  }
  /* The differing rows are the whole point of the table, so they carry the emphasis. */
  .carfax-vehicle-comparison tr.carfax-difference td {
    background-color: #fffbeb;
    font-weight: 600;
  }
</style>
